import { useAuth0 } from '@auth0/auth0-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button } from 'src/shared/components/ui/buttons/button';
import { Card, CardFooter } from 'src/shared/components/ui/card';
import { Separator } from 'src/shared/components/ui/separator';
import { cn } from 'src/shared/lib/utils';
import { useMutateUser } from 'src/shared/queries/useMe';

interface VerifyEmailProps {
  redirectUrl: string;
  dashboardUrl: string;
  className?: string;
  logout: () => void;
}

export default function VerifyEmail({ redirectUrl, dashboardUrl, className, logout }: VerifyEmailProps) {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { resendVerificationEmail } = useMutateUser();
  const { mutate, isPending, isSuccess, reset } = resendVerificationEmail;
  const [cooldownTime, setCooldownTime] = useState(0);

  useEffect(() => {
    if (isSuccess && cooldownTime === 0) {
      setCooldownTime(30);
    }
  }, [isSuccess, cooldownTime]);

  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined;
    if (cooldownTime > 0) {
      timerId = setTimeout(() => {
        setCooldownTime((prevTime) => prevTime - 1);
      }, 1000);
    } else if (cooldownTime === 0 && isSuccess) {
      reset();
    }
    return () => clearTimeout(timerId);
  }, [cooldownTime, isSuccess, reset]);

  useEffect(() => {
    if (user?.email_verified) {
      navigate({ to: redirectUrl });
    }
  }, [user?.email_verified, navigate]);

  return (
    <div>
      <img src="/static/images/tekst-logo.svg" width={80} height={20} className="mx-auto mb-4" alt="Tekst logo" />
      <Card className={cn('flex flex-col px-7 pt-6 pb-2 space-y-6', className)}>
        <div className=" w-full text-center">
          <p className="text-neutral-400">Email verification</p>
        </div>
        <div className="text-center text-neutral-950">
          <p>{!user?.email_verified ? 'Check your mailbox to verify your email' : 'Email verified successfully'}</p>
          <Separator className="my-4 w-[50%] mx-auto" />
          <p className="text-neutral-500 text-sm w-[70%] mx-auto">
            {!user?.email_verified
              ? 'Did not receive an email? Use the button below to send a new one.'
              : 'Your email is verified. Continue to your dashboard by clicking the button below.'}
          </p>
        </div>

        <CardFooter className="flex-center pb-0">
          <div>
            {!user?.email_verified ? (
              <Button
                disabled={isPending || cooldownTime > 0}
                onClick={() => {
                  if (!isPending && cooldownTime === 0) {
                    mutate();
                  }
                }}
              >
                {isPending
                  ? 'Sending...'
                  : cooldownTime > 0
                    ? `Email sent! Resend in ${cooldownTime}s`
                    : 'Resend verification email'}
              </Button>
            ) : (
              <Button onClick={() => navigate({ to: dashboardUrl, replace: true })}>Go to dashboard</Button>
            )}

            <Link to={'/login'} onClick={logout} className="mt-5 flex-center text-neutral-400 text-xs hover:underline">
              Back to login
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
